import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function CompoundInterestCalculator() {
  const [principal, setPrincipal] = useState("");
  const [rate, setRate] = useState("");
  const [time, setTime] = useState("");
  const [frequency, setFrequency] = useState("12");
  const [result, setResult] = useState<{ amount: number; interest: number } | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Finance", url: "https://calclab.app/finance" },
      { name: "Compound Interest Calculator", url: "https://calclab.app/calculator/compound-interest" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Compound Interest Calculator",
      "Calculate compound interest with different compounding frequencies. Free online calculator for investment growth calculations.",
      "https://calclab.app/calculator/compound-interest",
      lang
    )
  ]);

  const calculate = () => {
    const P = parseFloat(principal);
    const r = parseFloat(rate) / 100;
    const t = parseFloat(time);
    const n = parseFloat(frequency);

    if (isNaN(P) || isNaN(r) || isNaN(t) || isNaN(n) || P <= 0 || r <= 0 || t <= 0 || n <= 0) {
      toast.error("Please enter valid positive values");
      return;
    }

    const amount = P * Math.pow(1 + r / n, n * t);
    const interest = amount - P;

    setResult({ amount, interest });
    toast.success("Compound interest calculated!");
  };

  const reset = () => {
    setPrincipal("");
    setRate("");
    setTime("");
    setFrequency("12");
    setResult(null);
  };

  return (
    <PageLayout calculatorName="Compound Interest Calculator" calculatorPath="/calculator/compound-interest">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Compound Interest Calculator</h1>
          <p className="text-muted-foreground">
            Calculate compound interest with different compounding frequencies
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: A = P(1 + r/n)^(nt)</CardTitle>
            <CardDescription>
              Where A is final amount, P is principal, r is rate, n is compounding frequency, t is time
            </CardDescription>
          </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="principal">Principal Amount ($)</Label>
            <Input
              id="principal"
              type="number"
              placeholder="10000"
              value={principal}
              onChange={(e) => setPrincipal(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rate">Annual Interest Rate (%)</Label>
            <Input
              id="rate"
              type="number"
              placeholder="5"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="time">Time Period (years)</Label>
            <Input
              id="time"
              type="number"
              placeholder="5"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="frequency">Compounding Frequency</Label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Annually</SelectItem>
                <SelectItem value="2">Semi-annually</SelectItem>
                <SelectItem value="4">Quarterly</SelectItem>
                <SelectItem value="12">Monthly</SelectItem>
                <SelectItem value="365">Daily</SelectItem>
              </SelectContent>
            </Select>
          </div>
          </div>

          <div className="flex gap-3">
            <Button onClick={calculate} className="flex-1">
              <Calculator className="mr-2 h-4 w-4" />
              Calculate
            </Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result && (
            <>
              <Card className="bg-gradient-to-br from-emerald-500/10 to-green-500/5 border-emerald-500/20">
                <CardHeader>
                  <CardTitle className="text-xl">Results</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-lg break-all overflow-wrap-anywhere">
                      <strong>Final Amount:</strong> ${result.amount.toFixed(2)}
                    </p>
                    <CopyResultButton value={result.amount.toFixed(2)} />
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-lg break-all overflow-wrap-anywhere">
                      <strong>Interest Earned:</strong> ${result.interest.toFixed(2)}
                    </p>
                    <CopyResultButton value={result.interest.toFixed(2)} />
                  </div>
                </CardContent>
              </Card>

              <CalculatorSuperCalcCTA config={calculatorCTAs["compound-interest"]} trigger={result?.amount} />
            </>
          )}

          <Card className="bg-muted/50">
            <CardHeader>
              <CardTitle className="text-lg">How to use</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>1. Enter the principal amount (initial investment)</p>
              <p>2. Enter the annual interest rate as a percentage</p>
              <p>3. Enter the time period in years</p>
              <p>4. Select how often interest is compounded</p>
              <p>5. Click Calculate to see your investment growth</p>
            </CardContent>
          </Card>

          <Card className="bg-muted/50">
            <CardHeader>
              <CardTitle className="text-lg">Example</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p><strong>Problem:</strong> Invest $10,000 at 5% annually, compounded monthly for 5 years.</p>
              <p><strong>Solution:</strong></p>
              <div className="pl-4 space-y-1">
                <p>A = P(1 + r/n)^(nt)</p>
                <p>A = $10,000(1 + 0.05/12)^(12×5)</p>
                <p>A = $10,000(1.00417)^60</p>
                <p>A ≈ $12,833.59</p>
                <p>Interest = $12,833.59 - $10,000 = $2,833.59</p>
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
      </div>
    </PageLayout>
  );
}
